import React, { useState } from 'react';
import { useTranslation } from '../../utils/i18n';
import { CalibrationAnswers } from '../../types/athleteProfile';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ActivityIcon, MountainIcon, ArrowDownIcon, BatteryIcon, ThermometerIcon, DropletsIcon, SnowflakeIcon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, AlertTriangleIcon, TargetIcon } from 'lucide-react';
interface CalibrationWizardProps {
  initialAnswers: CalibrationAnswers;
  onSave: (answers: CalibrationAnswers) => void;
  onCancel: () => void;
}
interface QuestionConfig {
  key: keyof CalibrationAnswers;
  titleKey: string;
  descKey: string;
  icon: React.ElementType;
  options: {
    value: string;
    labelKey: string;
    descKey: string;
  }[];
}
const QUESTIONS: QuestionConfig[] = [{
  key: 'flatEffort',
  titleKey: 'calibration.q.flatEffort.title',
  descKey: 'calibration.q.flatEffort.desc',
  icon: ActivityIcon,
  options: [{
    value: 'sub530',
    labelKey: 'calibration.q.flatEffort.sub530',
    descKey: 'calibration.q.flatEffort.sub530Desc'
  }, {
    value: 'steady',
    labelKey: 'calibration.q.flatEffort.steady',
    descKey: 'calibration.q.flatEffort.steadyDesc'
  }, {
    value: 'comfortable',
    labelKey: 'calibration.q.flatEffort.comfortable',
    descKey: 'calibration.q.flatEffort.comfortableDesc'
  }, {
    value: 'easy',
    labelKey: 'calibration.q.flatEffort.easy',
    descKey: 'calibration.q.flatEffort.easyDesc'
  }]
}, {
  key: 'uphillStrategy',
  titleKey: 'calibration.q.uphillStrategy.title',
  descKey: 'calibration.q.uphillStrategy.desc',
  icon: MountainIcon,
  options: [{
    value: 'hikeEarly',
    labelKey: 'calibration.q.uphillStrategy.hikeEarly',
    descKey: 'calibration.q.uphillStrategy.hikeEarlyDesc'
  }, {
    value: 'mixed',
    labelKey: 'calibration.q.uphillStrategy.mixed',
    descKey: 'calibration.q.uphillStrategy.mixedDesc'
  }, {
    value: 'runMost',
    labelKey: 'calibration.q.uphillStrategy.runMost',
    descKey: 'calibration.q.uphillStrategy.runMostDesc'
  }]
}, {
  key: 'downhillHandling',
  titleKey: 'calibration.q.downhillHandling.title',
  descKey: 'calibration.q.downhillHandling.desc',
  icon: ArrowDownIcon,
  options: [{
    value: 'cautious',
    labelKey: 'calibration.q.downhillHandling.cautious',
    descKey: 'calibration.q.downhillHandling.cautiousDesc'
  }, {
    value: 'controlled',
    labelKey: 'calibration.q.downhillHandling.controlled',
    descKey: 'calibration.q.downhillHandling.controlledDesc'
  }, {
    value: 'aggressive',
    labelKey: 'calibration.q.downhillHandling.aggressive',
    descKey: 'calibration.q.downhillHandling.aggressiveDesc'
  }]
}, {
  key: 'fatigue',
  titleKey: 'calibration.q.fatigue.title',
  descKey: 'calibration.q.fatigue.desc',
  icon: BatteryIcon,
  options: [{
    value: 'early',
    labelKey: 'calibration.q.fatigue.early',
    descKey: 'calibration.q.fatigue.earlyDesc'
  }, {
    value: 'mid',
    labelKey: 'calibration.q.fatigue.mid',
    descKey: 'calibration.q.fatigue.midDesc'
  }, {
    value: 'late',
    labelKey: 'calibration.q.fatigue.late',
    descKey: 'calibration.q.fatigue.lateDesc'
  }, {
    value: 'ultra',
    labelKey: 'calibration.q.fatigue.ultra',
    descKey: 'calibration.q.fatigue.ultraDesc'
  }]
}, {
  key: 'riskTolerance',
  titleKey: 'calibration.q.riskTolerance.title',
  descKey: 'calibration.q.riskTolerance.desc',
  icon: AlertTriangleIcon,
  options: [{
    value: 'finish',
    labelKey: 'calibration.q.riskTolerance.finish',
    descKey: 'calibration.q.riskTolerance.finishDesc'
  }, {
    value: 'balanced',
    labelKey: 'calibration.q.riskTolerance.balanced',
    descKey: 'calibration.q.riskTolerance.balancedDesc'
  }, {
    value: 'performance',
    labelKey: 'calibration.q.riskTolerance.performance',
    descKey: 'calibration.q.riskTolerance.performanceDesc'
  }]
}, {
  key: 'heatTolerance',
  titleKey: 'calibration.q.heatTolerance.title',
  descKey: 'calibration.q.heatTolerance.desc',
  icon: ThermometerIcon,
  options: [{
    value: 'sensitive',
    labelKey: 'calibration.q.heatTolerance.sensitive',
    descKey: 'calibration.q.heatTolerance.sensitiveDesc'
  }, {
    value: 'moderate',
    labelKey: 'calibration.q.heatTolerance.moderate',
    descKey: 'calibration.q.heatTolerance.moderateDesc'
  }, {
    value: 'tolerant',
    labelKey: 'calibration.q.heatTolerance.tolerant',
    descKey: 'calibration.q.heatTolerance.tolerantDesc'
  }]
}, {
  key: 'sweatRate',
  titleKey: 'calibration.q.sweatRate.title',
  descKey: 'calibration.q.sweatRate.desc',
  icon: DropletsIcon,
  options: [{
    value: 'heavy',
    labelKey: 'calibration.q.sweatRate.heavy',
    descKey: 'calibration.q.sweatRate.heavyDesc'
  }, {
    value: 'normal',
    labelKey: 'calibration.q.sweatRate.normal',
    descKey: 'calibration.q.sweatRate.normalDesc'
  }, {
    value: 'low',
    labelKey: 'calibration.q.sweatRate.low',
    descKey: 'calibration.q.sweatRate.lowDesc'
  }]
}, {
  key: 'coldStart',
  titleKey: 'calibration.q.coldStart.title',
  descKey: 'calibration.q.coldStart.desc',
  icon: SnowflakeIcon,
  options: [{
    value: 'yes',
    labelKey: 'calibration.q.coldStart.yes',
    descKey: 'calibration.q.coldStart.yesDesc'
  }, {
    value: 'some',
    labelKey: 'calibration.q.coldStart.some',
    descKey: 'calibration.q.coldStart.someDesc'
  }, {
    value: 'no',
    labelKey: 'calibration.q.coldStart.no',
    descKey: 'calibration.q.coldStart.noDesc'
  }]
}];
const TOTAL_STEPS = QUESTIONS.length + 2;
export function CalibrationWizard({
  initialAnswers,
  onSave,
  onCancel
}: CalibrationWizardProps) {
  const {
    t
  } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<CalibrationAnswers>(initialAnswers);
  const isIntro = stepIndex === 0;
  const isReview = stepIndex === TOTAL_STEPS - 1;
  const questionIndex = stepIndex - 1;
  const currentQuestion = !isIntro && !isReview ? QUESTIONS[questionIndex] : null;
  const updateAnswer = <K extends keyof CalibrationAnswers,>(key: K, value: CalibrationAnswers[K]) => {
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleSelectAndAdvance = (value: string) => {
    if (currentQuestion) {
      updateAnswer(currentQuestion.key, value as any);
      setTimeout(() => {
        setStepIndex(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
      }, 200);
    }
  };
  const goBack = () => setStepIndex(prev => Math.max(prev - 1, 0));
  const goNext = () => setStepIndex(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
  const progressPercent = stepIndex / (TOTAL_STEPS - 1) * 100;
  const Option = ({
    selected,
    onClick,
    label,
    desc
  }: {
    selected: boolean;
    onClick: () => void;
    label: string;
    desc?: string;
  }) => <button onClick={onClick} className={`
        w-full text-left p-4 rounded-xl border transition-all relative
        ${selected ? 'border-[#FC4C02] bg-[#FFF5F0] ring-1 ring-[#FC4C02]' : 'border-[#E8E8ED] bg-white hover:border-[#D0D0D8] hover:bg-[#FAFAFA]'}
      `} data-id="element-948">
      <div className="flex justify-between items-start" data-id="element-949">
        <span className={`text-sm font-bold ${selected ? 'text-[#FC4C02]' : 'text-[#2D2D2D]'}`} data-id="element-950">
          {label}
        </span>
        {selected && <CheckIcon className="h-4 w-4 text-[#FC4C02]" data-id="element-951" />}
      </div>
      {desc && <p className={`text-xs mt-1 ${selected ? 'text-[#E34402]' : 'text-[#6B6B76]'}`} data-id="element-952">
          {desc}
        </p>}
    </button>;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-953">
      <Card className="w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" data-id="element-954">
        {/* Header with progress */}
        <div className="px-6 py-4 border-b border-[#E8E8ED] bg-white" data-id="element-955">
          <div className="flex justify-between items-center mb-3" data-id="element-956">
            <div data-id="element-957">
              <h2 className="text-lg font-bold text-[#2D2D2D]" data-id="element-958">
                {t('calibration.title')}
              </h2>
              <p className="text-xs text-[#6B6B76]" data-id="element-959">
                {isIntro ? t('calibration.welcome') : isReview ? t('calibration.review') : t('calibration.questionOf', {
                current: questionIndex + 1,
                total: QUESTIONS.length
              })}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onCancel} data-id="element-960">
              {t('common.cancel')}
            </Button>
          </div>
          <div className="w-full h-1.5 bg-[#F0F0F5] rounded-full overflow-hidden" data-id="element-961">
            <div className="h-full bg-[#FC4C02] rounded-full transition-all duration-300 ease-out" style={{
            width: `${progressPercent}%`
          }} data-id="element-962" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-[#FAFAFA]" data-id="element-963">
          {isIntro && <div className="text-center py-6 max-w-lg mx-auto" data-id="element-964">
              <div className="w-16 h-16 bg-[#FFF5F0] rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-965">
                <ActivityIcon className="h-8 w-8 text-[#FC4C02]" data-id="element-966" />
              </div>
              <h3 className="text-xl font-bold text-[#2D2D2D] mb-3" data-id="element-967">
                {t('calibration.introTitle')}
              </h3>
              <p className="text-[#6B6B76] mb-8 leading-relaxed" data-id="element-968">
                {t('calibration.introDesc')}
              </p>
              <div className="space-y-3 text-left bg-white p-4 rounded-xl border border-[#E8E8ED] mb-8" data-id="element-969">
                <div className="flex gap-3" data-id="element-970">
                  <CheckIcon className="h-5 w-5 text-[#00B843] shrink-0" data-id="element-971" />
                  <span className="text-sm text-[#2D2D2D]" data-id="element-972">
                    {t('calibration.benefit1')}
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-973">
                  <CheckIcon className="h-5 w-5 text-[#00B843] shrink-0" data-id="element-974" />
                  <span className="text-sm text-[#2D2D2D]" data-id="element-975">
                    {t('calibration.benefit2')}
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-976">
                  <CheckIcon className="h-5 w-5 text-[#00B843] shrink-0" data-id="element-977" />
                  <span className="text-sm text-[#2D2D2D]" data-id="element-978">
                    {t('calibration.benefit3')}
                  </span>
                </div>
              </div>
              <Button size="lg" onClick={goNext} className="w-full sm:w-auto" data-id="element-979">
                {t('calibration.start')}{' '}
                <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-980" />
              </Button>
            </div>}

          {currentQuestion && <div className="py-2" data-id="element-981">
              <div className="flex items-start gap-3 mb-6" data-id="element-982">
                <div className="p-2.5 bg-[#FFF5F0] rounded-xl text-[#FC4C02]" data-id="element-983">
                  <currentQuestion.icon className="h-6 w-6" data-id="element-984" />
                </div>
                <div data-id="element-985">
                  <h3 className="text-lg font-bold text-[#2D2D2D]" data-id="element-986">
                    {t(currentQuestion.titleKey)}
                  </h3>
                  <p className="text-sm text-[#6B6B76] mt-0.5" data-id="element-987">
                    {t(currentQuestion.descKey)}
                  </p>
                </div>
              </div>
              <div className="space-y-3" data-id="element-988">
                {currentQuestion.options.map(opt => <Option key={opt.value} label={t(opt.labelKey)} desc={t(opt.descKey)} selected={answers[currentQuestion.key] === opt.value} onClick={() => handleSelectAndAdvance(opt.value)} data-id="element-989" />)}
              </div>
            </div>}

          {isReview && <div className="text-center py-6" data-id="element-990">
              <div className="w-16 h-16 bg-[#ECFDF5] rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-991">
                <CheckIcon className="h-8 w-8 text-[#00B843]" data-id="element-992" />
              </div>
              <h3 className="text-xl font-bold text-[#2D2D2D] mb-3" data-id="element-993">
                {t('calibration.reviewTitle')}
              </h3>
              <p className="text-[#6B6B76] mb-8 max-w-md mx-auto" data-id="element-994">
                {t('calibration.reviewDesc')}
              </p>

              <div className="bg-white rounded-xl border border-[#E8E8ED] p-4 text-left max-w-md mx-auto mb-8 space-y-2" data-id="element-995">
                <div className="flex justify-between text-sm" data-id="element-996">
                  <span className="text-[#6B6B76]" data-id="element-997">
                    {t('calibration.flatPace')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-998">
                    {answers.flatEffort}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-999">
                  <span className="text-[#6B6B76]" data-id="element-1000">
                    {t('calibration.uphill')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1001">
                    {answers.uphillStrategy.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1002">
                  <span className="text-[#6B6B76]" data-id="element-1003">
                    {t('calibration.downhill')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1004">
                    {answers.downhillHandling}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1005">
                  <span className="text-[#6B6B76]" data-id="element-1006">
                    {t('calibration.fatigueOnset')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1007">
                    {answers.fatigue}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1008">
                  <span className="text-[#6B6B76]" data-id="element-1009">
                    {t('calibration.riskProfile')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1010">
                    {answers.riskTolerance}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1011">
                  <span className="text-[#6B6B76]" data-id="element-1012">
                    {t('athlete.heatTolerance')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1013">
                    {answers.heatTolerance}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1014">
                  <span className="text-[#6B6B76]" data-id="element-1015">
                    {t('athlete.sweatRate')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1016">
                    {answers.sweatRate}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1017">
                  <span className="text-[#6B6B76]" data-id="element-1018">
                    {t('calibration.coldStart')}
                  </span>
                  <span className="font-medium text-[#2D2D2D] capitalize" data-id="element-1019">
                    {answers.coldStart}
                  </span>
                </div>
              </div>

              <Button size="lg" onClick={() => onSave(answers)} className="w-full sm:w-auto bg-[#00B843] hover:bg-[#00A03A]" data-id="element-1020">
                {t('calibration.saveProfile')}
              </Button>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E8E8ED] bg-white flex justify-between" data-id="element-1021">
          {stepIndex > 0 ? <Button variant="ghost" onClick={goBack} data-id="element-1022">
              <ChevronLeftIcon className="h-4 w-4 mr-2" data-id="element-1023" /> {t('common.back')}
            </Button> : <div data-id="element-1024" />}

          {currentQuestion && <Button onClick={goNext} disabled={!answers[currentQuestion.key]} data-id="element-1025">
              {t('common.next')} <ChevronRightIcon className="h-4 w-4 ml-2" data-id="element-1026" />
            </Button>}
          {isIntro && <div data-id="element-1027" />}
        </div>
      </Card>
    </div>;
}